<template>
    <div
        class="tw-fixed tw-z-[9999] tw-bottom-5 tw-space-y-2 tw-w-80 tw-transition-all tw-duration-300 tw-ease-in-out"
        :class="{
            'tw-left-[300px]': sidebarExpanded,
            'tw-left-[90px]': !sidebarExpanded,
        }"
    >
        <transition-group name="toastlist" tag="div">
            <div
                v-for="toast in toasts"
                :key="toast.pushed_at"
                class="toast-glass tw-flex tw-select-none tw-items-start tw-gap-3 tw-rounded-2xl tw-px-4 tw-py-3 tw-text-sm tw-border tw-mt-2 tw-transition-all tw-duration-200 tw-relative tw-overflow-hidden"
                :class="{
                    'toast-success': toast.type === 'success',
                    'toast-error': ['error', 'danger'].includes(toast.type),
                    'toast-warning': toast.type === 'warning',
                    'toast-info': toast.type === 'info',
                }"
            >
                <!-- Icon -->
                <div class="tw-pt-[2px] tw-relative tw-z-10">
                    <svg v-if="toast.type === 'success'" class="tw-w-5 tw-h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                            fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293A1 1 0 003.293 10.707l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    <svg
                        v-else-if="['error', 'danger'].includes(toast.type)"
                        class="tw-w-5 tw-h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-5h2v2H9v-2zm0-6h2v5H9V7z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    <svg
                        v-else-if="toast.type === 'warning'"
                        class="tw-w-5 tw-h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.518 11.59c.75 1.334-.214 2.98-1.742 2.98H3.48c-1.528 0-2.492-1.646-1.742-2.98l6.519-11.59zM9 13h2v2H9v-2zm0-6h2v4H9V7z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    <svg v-else class="tw-w-5 tw-h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                            fill-rule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM9 9h2v4H9V9zm0-3h2v2H9V6z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </div>

                <!-- Content -->
                <div class="tw-flex-1 tw-min-w-0 tw-relative tw-z-10">
                    <div class="tw-flex tw-items-center tw-mb-0.5">
                        <strong class="tw-font-semibold tw-truncate">{{ toast.title }}</strong>
                        <button
                            class="tw-ml-auto tw-opacity-60 hover:tw-opacity-100 tw-transition-all tw-transform hover:tw-scale-110 active:tw-scale-95 tw-rounded-full hover:tw-bg-white/20 tw-p-1"
                            title="Close"
                            @click="removeToast(toast)"
                        >
                            <svg class="tw-w-3.5 tw-h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </button>
                    </div>
                    <p class="toast-content tw-text-xs tw-leading-snug tw-mb-1">
                        {{ toast.content }}
                    </p>
                </div>
            </div>
        </transition-group>
    </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useLocalStorage } from '@vueuse/core';

const toasts = ref([]);
const interval = ref(null);
const sidebarExpanded = useLocalStorage('sidebar.expanded', false);

onMounted(() => {
    addEventListener('toast', (e) => {
        e.detail.pushed_at = Date.now();
        toasts.value.push(e.detail);
    });
    interval.value = setInterval(processToasts, 1000);
});

onBeforeUnmount(() => {
    if (interval.value) clearInterval(interval.value);
});

function processToasts() {
    toasts.value = toasts.value.filter((t) => Date.now() - t.pushed_at < 10000);
}

function removeToast(toast) {
    toasts.value = toasts.value.filter((t) => t.pushed_at !== toast.pushed_at);
}
</script>
<style lang="scss" scoped>
.toast-glass {
    background: rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(20px) saturate(180%);
    -webkit-backdrop-filter: blur(20px) saturate(180%);
    box-shadow:
        0 8px 32px 0 rgba(0, 0, 0, 0.1),
        inset 0 1px 0 0 rgba(255, 255, 255, 0.5),
        0 4px 16px 0 rgba(0, 0, 0, 0.05);
}

.toast-glass::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 1rem;
    padding: 1px;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.1));
    -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
}

.toast-success {
    background: rgba(200, 255, 200, 0.2);
    border-color: rgba(34, 197, 94, 0.3);
    color: #166534;
}

.toast-error {
    background: rgba(255, 200, 200, 0.2);
    border-color: rgba(239, 68, 68, 0.3);
    color: #991b1b;
}

.toast-warning {
    background: rgba(255, 240, 200, 0.2);
    border-color: rgba(245, 158, 11, 0.3);
    color: #92400e;
}

.toast-info {
    background: rgba(200, 230, 255, 0.2);
    border-color: rgba(59, 130, 246, 0.3);
    color: #1e40af;
}

.toast-content {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
    white-space: normal;
}

.toastlist-enter-active {
    animation: toastSlideIn 0.25s ease-out;
}
.toastlist-leave-active {
    animation: toastSlideOut 0.15s ease-in forwards;
}
.toastlist-move {
    transition: transform 0.25s ease;
}

@keyframes toastSlideIn {
    0% {
        opacity: 0;
        transform: translateY(12px) scale(0.96);
    }
    100% {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@keyframes toastSlideOut {
    0% {
        opacity: 1;
        transform: translateX(0) scale(1);
    }
    100% {
        opacity: 0;
        transform: translateX(-15px) scale(0.96);
    }
}
</style>
